import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { CheckCircle, XCircle, Loader2, ArrowRight, Music, Calendar, MapPin, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const API = "/api";

const BookingSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [details, setDetails] = useState(null);

  useEffect(() => {
    if (sessionId) pollStatus(0);
    else setStatus('error');
  }, [sessionId]);

  const pollStatus = async (attempt) => {
    if (attempt >= 5) { setStatus('timeout'); return; }
    try {
      const res = await axios.get(`${API}/bookings/status/${sessionId}`);
      setDetails(res.data);
      if (res.data.payment_status === 'paid') setStatus('success');
      else if (res.data.status === 'expired') setStatus('expired');
      else setTimeout(() => pollStatus(attempt + 1), 2000);
    } catch {
      setTimeout(() => pollStatus(attempt + 1), 2000);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[60vh]" data-testid="booking-success-page">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-md">
        <Card className="bg-card/60 backdrop-blur border-border">
          <CardContent className="p-8 text-center space-y-4">
            {status === 'checking' && (
              <>
                <Loader2 size={48} className="mx-auto text-orange-400 animate-spin" />
                <h2 className="text-xl font-bold">Confirming your booking...</h2>
                <p className="text-sm text-muted-foreground">Please wait while we process the booking fee.</p>
              </>
            )}
            {status === 'success' && details && (
              <>
                <CheckCircle size={56} className="mx-auto text-green-500" />
                <h2 className="text-2xl font-bold">Booking Confirmed!</h2>
                <div className="space-y-2 text-sm text-left bg-muted/30 rounded-lg p-4">
                  <div className="flex items-center gap-2"><Music size={14} className="text-purple-400" /> <strong>{details.artist_name}</strong></div>
                  <div className="flex items-center gap-2"><Calendar size={14} className="text-blue-400" /> {details.event_date}</div>
                  <div className="flex items-center gap-2"><MapPin size={14} className="text-red-400" /> {details.event_venue}</div>
                  <div className="flex items-center gap-2"><DollarSign size={14} className="text-green-400" /> Artist Fee: ${details.agreed_amount?.toFixed(2)}</div>
                  <div className="flex items-center gap-2 text-orange-400"><DollarSign size={14} /> Booking Fee Paid: ${details.commission?.toFixed(2)}</div>
                </div>
                <p className="text-xs text-muted-foreground">
                  The artist will be notified. Please coordinate directly for event details and artist fee payment.
                </p>
                <Link to="/">
                  <Button className="w-full bg-purple-600 hover:bg-purple-500 gap-2 mt-2">
                    Back to Twin Tube <ArrowRight size={16} />
                  </Button>
                </Link>
              </>
            )}
            {(status === 'error' || status === 'expired' || status === 'timeout') && (
              <>
                <XCircle size={48} className="mx-auto text-red-400" />
                <h2 className="text-xl font-bold">{status === 'timeout' ? 'Still processing' : 'Something went wrong'}</h2>
                <p className="text-sm text-muted-foreground">
                  {status === 'timeout' ? 'Your booking may still be processing. Check your email.' : 'The booking could not be confirmed.'}
                </p>
                <Link to="/"><Button variant="outline" className="gap-2">Back to Twin Tube <ArrowRight size={16} /></Button></Link>
              </>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default BookingSuccess;
